/* eslint-disable import/extensions */
/* eslint-disable camelcase */
/* eslint-disable object-curly-newline */
import React, {
 useContext, useMemo, useState, useEffect, useRef, useCallback
} from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Icon, Input as KoodoInput, Typography } from '@telus/koodo-ui-kit'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { updateSimSelectionState } from '@apps/redux/features/SimSelectionSlice'
import { getBySlug } from '@apps/utils/contentful'
import {
  getSimSelectionPageContent,
  getSimSelectionSlugValue
} from '@apps/utils/simSelectionContent'
import { BrandName, ANALYTICS_EVENT_TYPES } from '@apps/utils/constants'
import { setPageAnalytics } from '@apps/utils/Analytics'
import PhoneDetails from '@/apps/components/PhoneDetails'
import InlineInstructionMarkdown from '@/apps/components/InlineInstructionMarkdown'
import useTagManagerInfo from '@/_engine/hooks/useTagManagerInfo'

const MAX_EID = 32
const MAX_EID_INPUT = 64
const MAX_IMEI = 15
const EID_INPUT_ID = 'input-imei'
const OPTIONAL_IMEI_INPUT_ID = 'input-optional-imei'
const SECTION_HEADING_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 20,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: '28px'
}
const INSTRUCTION_HEADING_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: '20px'
}
const BODY_COPY_TOKENS = {
  color: '#000000',
  fontSize: 14,
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: '20px'
}
const BODY_BOLD_TOKENS = {
  color: '#000000',
  fontName: 'StagSans',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: '20px'
}

const DealerESIMCompatibility = ({
  userType = 'dealer',
  eidValue,
  eidError = '',
  onEIDValueChange
}) => {
  const dispatch = useDispatch()
  const location = useLocation()

  const simSelectionState = useSelector((state) => state.simSelectionPage)
  const { pageContent } = useSelector((state) => state.common.content)
  const {
    user: { email }
  } = useSelector((state) => state.common)
  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region,
    [APPLICATION_STATE.SERVICES]: { getDeviceInfo }
  } = useContext(ApplicationContext)

  const { imei, eid } = simSelectionState
  const simSelectionPageContent = getSimSelectionPageContent(pageContent, language, userType)
  const { shortTextFields } = simSelectionPageContent

  const tagManagerInfo = useTagManagerInfo()
  const latestIMEIRef = useRef('')
  const lastLookupIMEIRef = useRef('')
  const initialDealerEIDRef = useRef(eid)
  const hasClearedInitialEIDRef = useRef(false)

  const [dealerEID, setDealerEID] = useState('')
  const [customerIMEI, setCustomerIMEI] = useState('')
  const [deviceInfo, setDeviceInfo] = useState()
  const [foundIMEI, setIMEIFound] = useState(null)
  const [loading, setLoading] = useState(false)
  const displayedEID = eidValue ?? dealerEID

  const pageTextContent = useMemo(() => {
    if (!shortTextFields) return {}
    return {
      beforeYouProceedHeading:
        getSimSelectionSlugValue(shortTextFields, 'beforeYouProceedHeading', userType),
      // Optional copy: strict dealer lookup would throw until the Contentful entry exists
      eSimBenefitsMessage: getBySlug(shortTextFields, 'eSimBenefitsMessage')?.value,
      eSimImeiInstructions:
        getSimSelectionSlugValue(shortTextFields, 'eSimImeiInstructions', userType),
      checkPhoneSettings:
        getSimSelectionSlugValue(shortTextFields, 'checkPhoneSettings', userType),
      dialPhoneCode:
        getSimSelectionSlugValue(shortTextFields, 'dialPhoneCode', userType),
      eidInputLabel:
        getSimSelectionSlugValue(shortTextFields, 'dealerEIDInputLabel', userType),
      eidInputPlaceholder:
        getSimSelectionSlugValue(shortTextFields, 'dealerEIDInputPlaceholder', userType),
      imeiInputLabel:
        getSimSelectionSlugValue(shortTextFields, 'dealerOptionalImeiInputLabel', userType),
      checkIMEI: getSimSelectionSlugValue(shortTextFields, 'checkIMEI', userType),
      imeiNotFound: getSimSelectionSlugValue(shortTextFields, 'imeiNotFound', userType)
    }
  }, [shortTextFields, userType])

  const updateIMEICompatibilityCheck = useCallback((newState) => {
    dispatch(updateSimSelectionState(newState))
  }, [dispatch])

  const pushClickAnalytics = ({
    click_text,
    journey_type,
    event_type,
    event_click = ANALYTICS_EVENT_TYPES.CLICK
  } = {}) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(location, 'click', region, language, {
        ...tagManagerInfo,
        click_text,
        journey_type
      }, event_type, event_click)
    )
  }

  const getInputValue = (value) => value?.target?.value ?? value ?? ''

  const sanitizeNumber = (value, maxLength) => `${getInputValue(value)}`.replace(/\D/g, '').slice(0, maxLength)

  const isIMEIComplete = (value) => value.length >= 14 && value.length <= MAX_IMEI

  const onEIDNumberChange = (value) => {
    const sanitizedEIDValue = sanitizeNumber(value, MAX_EID)
    setDealerEID(sanitizedEIDValue)
    if (onEIDValueChange) onEIDValueChange(sanitizedEIDValue)
    updateIMEICompatibilityCheck({
      eid: sanitizedEIDValue.length === MAX_EID ? sanitizedEIDValue : ''
    })
  }

  useEffect(() => {
    // Clear only an EID restored before this screen mounted; newly scanned EIDs must remain.
    if (hasClearedInitialEIDRef.current || !initialDealerEIDRef.current) return
    hasClearedInitialEIDRef.current = true
    setDealerEID('')
    if (onEIDValueChange) onEIDValueChange('')
    updateIMEICompatibilityCheck({ eid: '' })
  }, [onEIDValueChange, updateIMEICompatibilityCheck])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (typeof document !== 'undefined') {
        document.getElementById(EID_INPUT_ID)?.focus()
      }
    }, 0)

    return () => clearTimeout(timer)
  }, [])

  const onIMEIChange = (value) => {
    const imeiValue = sanitizeNumber(value, MAX_IMEI)
    setCustomerIMEI(imeiValue)
    latestIMEIRef.current = imeiValue

    if (!isIMEIComplete(imeiValue)) {
      lastLookupIMEIRef.current = ''
      setIMEIFound(null)
      setDeviceInfo(undefined)
      setLoading(false)
    }

    if (imeiValue.length === 0 || imei) {
      updateIMEICompatibilityCheck({
        imei: imeiValue,
        imeiChecked: false,
        imeiCompatible: false
      })
    }
  }

  const handleKeyPress = (event) => {
    if (event.ctrlKey || event.metaKey) return
    if (event.key?.length === 1 && !/[0-9]/.test(event.key)) {
      event.preventDefault()
    }
  }

  const lookupIMEI = async (imeiToLookup) => {
    const parsedIMEI = imeiToLookup.trim()
    if (!isIMEIComplete(parsedIMEI)) return

    pushClickAnalytics({ click_text: pageTextContent?.checkIMEI || 'Check IMEI' })
    setLoading(true)

    try {
      const deviceInfoResponse = await getDeviceInfo(BrandName, parsedIMEI)
      const { data = {} } = deviceInfoResponse || {}
      const {
        imageURL,
        enabledESIM,
        voLteSupportStatusCode,
        manufacturerName,
        modelName
      } = data

      const isIMEIFound = Boolean(manufacturerName && modelName)
      const isIMEICompatible = enabledESIM?.toUpperCase() === 'TRUE'

      if (latestIMEIRef.current !== parsedIMEI) return

      setIMEIFound(isIMEIFound)
      updateIMEICompatibilityCheck({
        imei: parsedIMEI,
        imeiChecked: isIMEIFound,
        imeiCompatible: isIMEICompatible
      })

      setDeviceInfo({
        imageURL,
        eSimCompatible: isIMEICompatible,
        lteEnabled: voLteSupportStatusCode === 'TRUE',
        manufacturer: manufacturerName,
        model: modelName
      })
    } catch {
      if (latestIMEIRef.current === parsedIMEI) {
        setIMEIFound(false)
      }
    } finally {
      if (latestIMEIRef.current === parsedIMEI) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!isIMEIComplete(customerIMEI)) return undefined
    if (lastLookupIMEIRef.current === customerIMEI) return undefined

    const timer = setTimeout(() => {
      lastLookupIMEIRef.current = customerIMEI
      lookupIMEI(customerIMEI)
    }, 400)

    return () => clearTimeout(timer)
  }, [customerIMEI])

  const imeiLookupSucceeded = () => foundIMEI === true
  const imeiLookupFailed = () => foundIMEI === false

  const getIMEIInputValidation = () => {
    if (imeiLookupFailed()) return 'error'
    if (imeiLookupSucceeded()) return 'success'
    return undefined
  }

  const inputTokens = {
    borderRadius: 4
  }

  const eidInputTokens = {
    ...inputTokens,
    paddingRight: 56
  }

  const eidErrorInputTokens = {
    ...eidInputTokens,
    backgroundColor: '#ffffff',
    borderColor: '#c9370b',
    borderWidth: 1,
    outerBorderColor: 'transparent',
    outerBorderWidth: 0,
    icon: null,
    iconColor: 'rgba(0, 0, 0, 0)',
    validationIcon: null,
    validationIconColor: 'rgba(0, 0, 0, 0)'
  }

  const renderEIDTrailingIcon = () => eidError ? (
    <span
      className="pointer-events-none absolute bottom-[10px] right-4 flex h-6 w-6 items-center justify-center"
      data-testid="dealer-eid-error-icon"
      aria-hidden="true"
    >
      <Icon icon="ExclamationOctagon" variant={{ color: 'danger', size: 'small' }} />
    </span>
  ) : (
    <span
      className="pointer-events-none absolute bottom-[10px] right-5 flex h-6 w-6 items-center justify-center"
      data-testid="dealer-eid-scan-icon"
      aria-hidden="true"
    >
      <Icon icon="EidScanFrame" />
    </span>
  )

  const renderIMEIFeedback = () => {
    if (imeiLookupSucceeded() && deviceInfo) {
      return (
        <div className="mt-6">
          <PhoneDetails
            phoneImage={deviceInfo?.imageURL || ''}
            eSimCompatible={deviceInfo?.eSimCompatible}
            lteCompatible={deviceInfo?.lteEnabled}
            imei={customerIMEI}
            manufacturer={deviceInfo?.manufacturer}
            model={deviceInfo?.model}
            layout="panel"
            showIncompatibleMessage={false}
            userType={userType}
          />
        </div>
      )
    }

    if (!imeiLookupFailed()) return null

    return (
      <div
        className="mt-1 w-full bg-[#fbd1c5] px-4 py-3 text-black"
        data-testid="dealer-imei-not-found-feedback"
        role="alert"
      >
        <Typography
          variant={{ size: 14, bold: true, color: '#000000' }}
          tokens={BODY_BOLD_TOKENS}
        >
          {pageTextContent?.imeiNotFound}
        </Typography>
      </div>
    )
  }

  return (
    <div className="text-black" style={{ color: '#000000' }}>
      <div
        className="mb-4"
        data-contentful-slug="beforeYouProceedHeading"
      >
        <Typography
          variant={{ size: 20, bold: true, color: '#000000' }}
          tokens={SECTION_HEADING_TOKENS}
        >
          {pageTextContent?.beforeYouProceedHeading}
        </Typography>
      </div>
      <div className="kpre-esim-instruction-panel mb-6 bg-[#f2f2f2] px-6 py-5 text-black">
        {pageTextContent?.eSimBenefitsMessage && (
          <div
            className="kpre-esim-benefits-message mb-4"
            data-contentful-slug="eSimBenefitsMessage"
          >
            <Typography
              variant={{ size: 14, color: '#000000' }}
              tokens={BODY_COPY_TOKENS}
            >
              {pageTextContent.eSimBenefitsMessage}
            </Typography>
          </div>
        )}
        <div
          className="kpre-esim-instruction-heading mb-4"
          data-contentful-slug="eSimImeiInstructions"
        >
          <Typography
            variant={{ size: 14, color: '#000000' }}
            tokens={INSTRUCTION_HEADING_TOKENS}
          >
            {pageTextContent?.eSimImeiInstructions}
          </Typography>
        </div>
        <ul className="kpre-esim-instruction-list list-outside list-disc space-y-1 pl-5 text-black">
          <li data-contentful-slug="checkPhoneSettings">
            <Typography
              variant={{ size: 14, color: '#000000' }}
              tokens={BODY_COPY_TOKENS}
            >
              <InlineInstructionMarkdown>
                {pageTextContent?.checkPhoneSettings}
              </InlineInstructionMarkdown>
            </Typography>
          </li>
          <li data-contentful-slug="dialPhoneCode">
            <Typography
              variant={{ size: 14, color: '#000000' }}
              tokens={BODY_COPY_TOKENS}
            >
              <InlineInstructionMarkdown>
                {pageTextContent?.dialPhoneCode}
              </InlineInstructionMarkdown>
            </Typography>
          </li>
        </ul>
      </div>

      <div className="mb-6">
        <div className={`dealer-eid-input-field relative w-full ${eidError ? 'kpre-imei-error-input' : ''}`}>
          <KoodoInput
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            id={EID_INPUT_ID}
            nativeID={EID_INPUT_ID}
            label={pageTextContent?.eidInputLabel}
            placeholder={pageTextContent?.eidInputPlaceholder}
            maxLength={MAX_EID_INPUT}
            value={displayedEID}
            onChange={onEIDNumberChange}
            onKeyPress={handleKeyPress}
            onKeyDown={handleKeyPress}
            validation={eidError ? 'error' : undefined}
            tokens={eidError ? eidErrorInputTokens : eidInputTokens}
            height={44}
            data-contentful-slug="dealerEIDInputLabel"
            testId="sim-selection-enter-your-eid-input"
            autoFocus
          />
          {renderEIDTrailingIcon()}
        </div>
        {eidError && (
          <div
            className="mt-1 w-full bg-[#fbd1c5] px-4 py-3 text-black"
            data-testid="dealer-eid-error-feedback"
            role="alert"
          >
            <Typography
              variant={{ size: 14, color: '#000000' }}
              tokens={BODY_COPY_TOKENS}
            >
              {eidError}
            </Typography>
          </div>
        )}
      </div>

      <div className="mb-0">
        <KoodoInput
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          id={OPTIONAL_IMEI_INPUT_ID}
          nativeID={OPTIONAL_IMEI_INPUT_ID}
          label={pageTextContent?.imeiInputLabel}
          maxLength={MAX_IMEI}
          value={customerIMEI}
          onChange={onIMEIChange}
          onKeyDown={handleKeyPress}
          validation={getIMEIInputValidation()}
          tokens={inputTokens}
          height={44}
          loading={loading}
          data-contentful-slug="dealerOptionalImeiInputLabel"
          testId="sim-selection-enter-your-imei-input"
        />
      </div>
      {renderIMEIFeedback()}
    </div>
  )
}

DealerESIMCompatibility.propTypes = {
  userType: PropTypes.string,
  eidValue: PropTypes.string,
  eidError: PropTypes.string,
  onEIDValueChange: PropTypes.func
}

export default DealerESIMCompatibility
